import React, { createContext, useState, useContext, useEffect } from "react";
import { DemoDataService } from "../../services/DemoDataService";
import DemoSignatureService from "../../services/DemoSignatureService";

// Crear el contexto
const DemoContext = createContext();

export const DemoProvider = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isLoadingDemo, setIsLoadingDemo] = useState(false);
  const [demoData, setDemoData] = useState(null);
  const [demoError, setDemoError] = useState(null);
  const [currentDemoStep, setCurrentDemoStep] = useState(0);
  const [demoTourActive, setDemoTourActive] = useState(false);

  // Función para inicializar la demo
  const startDemo = async () => {
    setIsLoadingDemo(true);
    setDemoError(null);

    try {
      // Cargar datos mock en IndexedDB
      await DemoDataService.initializeDemoData();

      // Obtener datos de demo
      const mockData = await DemoDataService.getDemoData();
      setDemoData(mockData);

      // Configurar estado de demo
      setIsDemoMode(true);
      sessionStorage.setItem("isDemoMode", "true");
      sessionStorage.setItem("RolDevelop", "3"); // Supervisor
      sessionStorage.setItem("PlantaDevelop", "2"); // Planta demo changed to ID 2

      console.log("🎬 Demo iniciada exitosamente");
      console.log(
        "🏭 Planta configurada para demo:",
        sessionStorage.getItem("PlantaDevelop")
      );
    } catch (error) {
      console.error("Error al inicializar demo:", error);
      setDemoError("Error al cargar la demostración");
    } finally {
      setIsLoadingDemo(false);
    }
  };

  // Función para reiniciar la demo
  const restartDemo = async () => {
    setIsLoadingDemo(true);
    setDemoError(null);

    try {
      // Limpiar datos existentes
      await DemoDataService.clearDemoData();

      // Reinicializar datos
      await DemoDataService.initializeDemoData();
      const mockData = await DemoDataService.getDemoData();
      setDemoData(mockData);

      // Resetear estados
      setCurrentDemoStep(0);
      setDemoTourActive(false);

      console.log("🔄 Demo reiniciada exitosamente");
    } catch (error) {
      console.error("Error al reiniciar demo:", error);
      setDemoError("Error al reiniciar la demostración");
    } finally {
      setIsLoadingDemo(false);
    }
  };

  // Función para salir de la demo
  const exitDemo = async () => {
    try {
      // Limpiar datos de demo
      await DemoDataService.clearDemoData();

      // Limpiar firmas locales
      DemoSignatureService.clearLocalSignatures();

      // Limpiar estado de sesión del modo demo
      sessionStorage.removeItem("isDemoMode");
      sessionStorage.removeItem("RolDevelop");
      sessionStorage.removeItem("PlantaDevelop");

      // Resetear estados
      setIsDemoMode(false);
      setDemoData(null);
      setCurrentDemoStep(0);
      setDemoTourActive(false);
      setDemoError(null);

      console.log(
        "🚪 Demo finalizada - Rol restaurado a usuario default y firmas locales limpiadas"
      );
    } catch (error) {
      console.error("Error al salir de demo:", error);
    }
  };

  // Función para avanzar en el tour guiado
  const nextDemoStep = () => {
    setCurrentDemoStep((prev) => prev + 1);
  };

  // Función para retroceder en el tour guiado
  const previousDemoStep = () => {
    setCurrentDemoStep((prev) => Math.max(0, prev - 1));
  };

  // Función para activar/desactivar tour guiado
  const toggleDemoTour = () => {
    setDemoTourActive((prev) => !prev);
    if (!demoTourActive) {
      setCurrentDemoStep(0);
    }
  };

  // Verificar si estamos en modo demo al cargar
  useEffect(() => {
    const checkDemoMode = async () => {
      const isDemoActive = sessionStorage.getItem("isDemoMode") === "true";

      if (isDemoActive) {
        try {
          const mockData = await DemoDataService.getDemoData();
          if (mockData) {
            setIsDemoMode(true);
            setDemoData(mockData);

            // Asegurar que las variables de sesión estén configuradas
            if (!sessionStorage.getItem("PlantaDevelop")) {
              sessionStorage.setItem("PlantaDevelop", "2");
            }
            if (!sessionStorage.getItem("RolDevelop")) {
              sessionStorage.setItem("RolDevelop", "3");
            }

            console.log("🎬 Demo restaurada desde sesión");
            console.log(
              "🏭 Planta ID:",
              sessionStorage.getItem("PlantaDevelop")
            );
          } else {
            // Si no hay datos, salir del modo demo
            exitDemo();
          }
        } catch (error) {
          console.error("Error al restaurar demo:", error);
          exitDemo();
        }
      }
    };

    checkDemoMode();
  }, []);

  // Función para obtener datos específicos de la demo
  const getDemoReports = () => {
    return demoData?.reports || [];
  };

  const getDemoStatistics = () => {
    return demoData?.statistics || {};
  };

  const getDemoPlantInfo = () => {
    return demoData?.plantInfo || {};
  };

  return (
    <DemoContext.Provider
      value={{
        // Estados
        isDemoMode,
        isLoadingDemo,
        demoData,
        demoError,
        currentDemoStep,
        demoTourActive,

        // Funciones de control
        startDemo,
        restartDemo,
        exitDemo,

        // Funciones de tour
        nextDemoStep,
        previousDemoStep,
        toggleDemoTour,

        // Funciones de datos
        getDemoReports,
        getDemoStatistics,
        getDemoPlantInfo,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error("useDemo debe ser usado dentro de un DemoProvider");
  }
  return context;
};

export default DemoContext;
